<template>
  <div
    class="drawer"
    :class="{ 'drawer--open': isOpen }"
  >
    <Header :isOpen="isOpen" @toggleVisibility="handleToggleVisibility" />

    <div v-show="isOpen">
      <v-tabs
        v-model="activeTab"
        centered
        slider-color="secondary"
        class="v-tabs--webcall"
      >
        <v-tab
          v-for="(item, index) in ['callAcar', 'activeRides']"
          :key="index"
          ripple
          :class="{ 'get-height': index === 0 }"
          :data-id="item"
        >
          {{ $t(`${namespace}.tabs.${item}`) }}
        </v-tab>

        <v-tab-item>
          <Form
            @hideMapOverlay="hideMapOverlay"
            @updateRidesList="updateRidesList"
            @resetMap="resetMap"
          />
        </v-tab-item>

        <v-tab-item>
          <RideList
            :rides="rides"
            :activeTab="activeTab"
            :activeRide="activeRide"
            @changeTab="handleChangeTab"
            @rideEvent="rideEvent"
            @resetMap="resetMap"
          />
        </v-tab-item>
      </v-tabs>
    </div>

    <CancelRideDialog
      :visible="cancelRideDialog"
      :ride="selectedRide"
      @closeDialog="closeCancelRideDialog"
      @updateRidesList="reset"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { isArray } from 'utils/functions'
import Header from 'components/newWebCall/drawer/Header'
import Form from 'components/newWebCall/drawer/Form'
import RideList from 'components/newWebCall/drawer/rideList'
import CancelRideDialog from 'components/newWebCall/drawer/CancelRideDialog'

export default {
  name: 'Drawer',
  components: {
    Header,
    Form,
    RideList,
    CancelRideDialog
  },
  data () {
    return {
      namespace: 'Views.NewWebCallPage',
      activeTab: 0,
      isOpen: true,
      rides: [],
      debugMode: false,
      firstLoaded: true,
      setTimeoutId: null,
      requestTimeout: 3000,
      targetRideId: null,
      activeRide: null,
      cancelRideDialog: false,
      selectedRide: null,
      exsit: false
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    hideMapOverlay () {
      this.$emit('hideMapOverlay')
    },

    handleToggleVisibility () {
      this.isOpen = !this.isOpen
      this.$emit('toggleVisibility', this.isOpen)
    },

    handleChangeTab (tab) {
      this.activeTab = tab
    },

    async getWebCallHistory () {
      try {
        const { data: res } = this.debugMode
          ? await this.$apiService.getWebCallHistoryMock()
          : await this.$apiService.getWebCallHistory()

        this.rides = res && this.isValidArray(res.data) ? res.data : []
        const hasActiveRides = Boolean(this.rides.length)

        !hasActiveRides && this.resetMap()
        await this.getActiveRide()
        this.updateActiveRideMarkers()
        this.changeTabWhenHasActiveRides(hasActiveRides)
        this.getWebCallHistoryAutoRequest(hasActiveRides, this.getWebCallHistory, this.requestTimeout)
      } catch (error) {
        console.log('ERROR:', error)
      }
    },

    resetMap () {
      this.$emit('resetMap')
    },

    // [TO DO WEBCALL]
    async getActiveRide () {
      try {
        this.activeRide = this.rides.find(ride => {
          const rideId = ride && ride.running && ride.running.rideID
            ? ride.running.rideID
            : null
          return rideId === this.targetRideId
        })

        if (this.activeRide) {
          const { data: res } = await this.$apiService.getWebCallById(this.activeRide.running.rideID)
          this.activeRide = res && res.data
            ? { ...this.activeRide, ...res.data }
            : { ...this.activeRide }
        }
      } catch (error) {
        console.log('ERROR:', error)
      }
    },

    updateActiveRideMarkers () {
      if (this.activeRide) {
        this.hideMapOverlay()

        const { from, to, running, status } = this.activeRide
        const driver = { address: null, ...running.driver.position }
        const origin = { address: from.street, ...from }
        const destiny = { address: to.street, ...to }

        if (
          status === 'COULDNT_FIND_AVAILABLE_DRIVERS' ||
          status === 'DRIVERS_REJECTED' ||
          status === 'CANCELED_BY_DRIVER' ||
          status === 'CANCELED_BY_PASSENGER'
        ) {
          setTimeout(() => this.reset(), 2000)
          return
        }

        if (status === 'CAR_ON_THE_WAY' || status === 'CAR_ARRIVED') {
          this.$gmaps.renderMarkers({
            from: { ref: 'origin', ...driver },
            to: { ref: 'destiny', ...origin },
            adjustMarkerType: 'trackRide'
          })
          return
        }

        if (status === 'IN_PROGRESS') {
          this.$gmaps.renderMarkers({
            from: { ref: 'origin', ...driver },
            to: { ref: 'destiny', ...destiny },
            adjustMarkerType: 'trackRide'
          })
          return
        }

        if (status === 'RIDE_ENDED') {
          this.$gmaps.renderMarkers({
            from: { ref: 'origin', ...origin },
            to: { ref: 'destiny', ...destiny },
            adjustMarkerType: 'originDestiny'
          })
        }
      }
    },

    changeTabWhenHasActiveRides (hasActiveRides) {
      if (this.firstLoaded) {
        hasActiveRides && this.handleChangeTab(1)
        this.firstLoaded = false
      }
    },

    getWebCallHistoryAutoRequest (hasActiveRides, func, requestTimeout) {
      if (hasActiveRides && this.exsit) {
        this.timeoutId = setTimeout(func, requestTimeout)
        return
      }

      this.clearWebCallHistoryAutoRequest()
    },

    clearWebCallHistoryAutoRequest () {
      clearTimeout(this.timeoutId)
    },

    async rideEvent ({ event, ride, rideId }) {
      if (event === 'discard') {
        this.clearWebCallHistoryAutoRequest()
        this.getWebCallHistory()
        this.setTargetRideId(null)
        return
      }

      if (event === 'cancel') {
        this.selectedRide = { ...ride }
        this.openCancelRideDialog()
        return
      }

      if (event === 'callAgain') {
        try {
          await this.$apiService.removeNotification(rideId)
          await this.$apiService.callCar(ride)

          // reset auto request
          this.clearWebCallHistoryAutoRequest()
          this.getWebCallHistory()
        } catch (error) {
          console.log('ERROR', error)

          this.showAlert({
            type: 'error',
            message: this.$t(`${this.namespace}.reCallError`),
            timeout: 8000
          })
        }

        return
      }

      this.setTargetRideId(rideId)
      await this.getActiveRide()
      this.updateActiveRideMarkers()
    },

    setTargetRideId (id = null) {
      this.targetRideId = id
    },

    updateRidesList () {
      this.clearWebCallHistoryAutoRequest()
      this.getWebCallHistory()
      setTimeout(() => this.handleChangeTab(1), 1000)
    },

    openCancelRideDialog () {
      this.cancelRideDialog = true
    },

    closeCancelRideDialog () {
      this.cancelRideDialog = false
      this.selectedRide = null
    },

    reset () {
      this.clearWebCallHistoryAutoRequest()
      this.getWebCallHistory()
      this.setTargetRideId(null)
      this.resetMap()
    }
  },
  watch: {
    // [TO DO WEBCALL]
    activeTab (to, from) {
      if (to === 0 && from === 1 && Boolean(this.activeRide)) {
        this.reset()
      }
    }
  },
  mounted () {
    this.exsit = true
    this.getWebCallHistory()
  },
  beforeDestroy () {
    this.exsit = false
    this.clearWebCallHistoryAutoRequest()
  }
}
</script>

<style lang="scss" scoped>
.drawer {
  background-color: #fff;
  bottom: 0;
  box-shadow: 0px 4px 5px -2px rgba(#000, .2),
    0px 7px 10px 1px rgba(#000, .14),
    0px 2px 16px 1px rgba(#000, .12) !important;
  position: fixed;
  right: 0;
  top: 0;
  transition: width .16s;
  width: 60px;
  z-index: 10;

  &.drawer--open {
    width: 360px;
  }
}
</style>

<style lang="scss">
.v-tabs.v-tabs--webcall {
  overflow: hidden;

  .v-tabs__container {
    margin-left: 24px;
    margin-right: 24px;
    height: 64px;
  }

  .v-tabs__div {
    font-size: 14px;
    text-transform: none;

    .v-tabs__item {
      white-space: nowrap;
    }

    .v-tabs__item--active {
      font-weight: 600;
    }
  }

  .v-tabs__slider {
    height: 4px;

    &.secondary {
      background: linear-gradient(272deg, $accent, $accent-2) !important;
      border: none !important;
    }
  }
}
</style>
